import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Stack
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Error as ErrorIcon,
  Delete,
  Save,
  Summarize,
  ExpandMore
} from '@mui/icons-material';
import aiService from '../../services/aiService';
import analysisStorageService from '../../services/analysisStorageService';
import photoUploadService from '../../services/photoUploadService';

/**
 * Enhanced AI Photo Analyzer Component with Auto-Save
 * Supports multiple photo uploads with inline AI analysis and automatic database storage
 */
const AIPhotoAnalyzerEnhanced = ({
  onAnalysisComplete,
  onPhotosChanged,
  autoSave = true,
  projectId = null,
  verificationRequestId = null,
  initialPhotos = null
}) => {
  const [photos, setPhotos] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(false);

  const loadSavedAnalyses = async () => {
    setLoadingSaved(true);
    try {
      const filters = {
        type: 'photos',
        projectId: projectId || null,
        verificationRequestId: verificationRequestId || null
      };

      const data = await analysisStorageService.getAnalyses(filters);
      console.log('Loaded saved analyses:', {
        totalPhotos: data.photos?.length || 0,
        photos: data.photos?.map(p => ({
          id: p.id,
          name: p.photo_name,
          hasAnalysis: !!p.analysis,
          analysisType: typeof p.analysis,
          analysisPreview: p.analysis ? (typeof p.analysis === 'string' ? p.analysis.substring(0, 100) : Object.keys(p.analysis)) : null
        }))
      });
      
      const savedPhotos = (data.photos || []).map(photo => {
        // Parse analysis if it's a string (backend may return it as string or object)
        let analysis = null;
        try {
          if (photo.analysis) {
            if (typeof photo.analysis === 'string') {
              // Try to parse as JSON
              try {
                analysis = JSON.parse(photo.analysis);
              } catch (e) {
                console.warn('Failed to parse analysis string for photo:', photo.id, e);
                analysis = null;
              }
            } else if (typeof photo.analysis === 'object' && photo.analysis !== null) {
              // Already an object, use it directly
              analysis = photo.analysis;
            }
          } else {
            console.warn('Photo has no analysis field:', photo.id, photo.photo_name);
          }
        } catch (parseError) {
          console.error('Error parsing analysis for photo:', photo.id, parseError, photo);
          analysis = null;
        }

        console.log('Photo processed:', {
          id: photo.id,
          name: photo.photo_name,
          hasAnalysis: !!analysis,
          analysisKeys: analysis ? Object.keys(analysis) : null,
          analysisPreview: analysis ? JSON.stringify(analysis).substring(0, 200) : null
        });

        return {
          id: photo.id,
          file: null, // No file object for saved photos
          name: photo.photo_name || 'Unknown',
          previewUrl: photo.thumbnail_url || photo.photo_url,
          photoUrl: photo.photo_url,
          status: 'saved',
          analysis: analysis,
          savedId: photo.id,
          createdAt: photo.created_at
        };
      });

      // Only add if we have saved photos and they're not already in state
      if (savedPhotos.length > 0) {
        setPhotos(prev => {
          // Merge with existing, avoiding duplicates
          const existingIds = new Set(prev.map(p => p.savedId).filter(Boolean));
          const newPhotos = savedPhotos.filter(p => !existingIds.has(p.savedId));
          return [...prev, ...newPhotos];
        });
      }
    } catch (err) {
      console.error('Error loading saved analyses:', err);
      // Don't show error to user - this is just for persistence
    } finally {
      setLoadingSaved(false);
    }
  };

  // Seed previously-saved photos passed down by the parent (resume flow).
  // Runs once on mount so the verifier sees their earlier photos.
  useEffect(() => {
    if (Array.isArray(initialPhotos) && initialPhotos.length > 0) {
      setPhotos(initialPhotos);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load saved analyses on mount and when filters change. Only query when we
  // have a real scope key — a null/null query is meaningless and would pull in
  // unrelated photos. The structured workflow uses initialPhotos instead.
  useEffect(() => {
    if (autoSave && (projectId || verificationRequestId)) {
      loadSavedAnalyses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, verificationRequestId, autoSave]);

  // Notify parent whenever the photo list changes (upload, delete, or load saved)
  useEffect(() => {
    if (typeof onPhotosChanged === 'function') {
      onPhotosChanged(photos);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos]);

  const handlePhotoUpload = async (event) => {
    const files = Array.from(event.target.files);
    
    if (files.length === 0) return;

    if (files.length > 10) {
      setError('Maximum 10 photos can be uploaded at once');
      return;
    }

    setAnalyzing(true);
    setError(null);

    const newPhotos = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      try {
        // Videos are attached as evidence but NOT AI-analysed (analysis is
        // image-only). Upload to S3 and add to the same photos array so they
        // persist and render alongside photos in the report.
        if (file.type.startsWith('video/')) {
          const videoId = `temp_${Date.now()}_${i}`;
          const videoPreview = URL.createObjectURL(file);
          const videoData = {
            id: videoId, file, name: file.name, previewUrl: videoPreview,
            status: 'analyzing', analysis: null, savedId: null, isVideo: true
          };
          newPhotos.push(videoData);
          setPhotos(prev => [...prev, videoData]);
          try {
            const uploadResult = await photoUploadService.uploadMedia(file);
            const mediaUrl = uploadResult?.photoUrl;
            if (!mediaUrl) throw new Error('Video upload succeeded but no URL returned');
            setPhotos(prev => prev.map(p => p.id === videoId
              ? { ...p, status: 'saved', isVideo: true, photoUrl: mediaUrl, thumbnailUrl: mediaUrl }
              : p));
          } catch (uploadError) {
            const msg = uploadError?.message || 'Video upload failed';
            setPhotos(prev => prev.map(p => p.id === videoId ? { ...p, status: 'save_failed', errorMessage: msg } : p));
            setError(`Failed to upload ${file.name}: ${msg}`);
          }
          continue;
        }

        // Create preview URL
        const previewUrl = URL.createObjectURL(file);

        // Add photo with pending status
        const photoId = `temp_${Date.now()}_${i}`;
        const photoData = {
          id: photoId,
          file,
          name: file.name,
          previewUrl,
          status: 'analyzing',
          analysis: null,
          savedId: null
        };

        newPhotos.push(photoData);
        setPhotos(prev => [...prev, photoData]);

        // Analyze photo with AI - pass File object directly, not base64
        const analysis = await aiService.analyzePhoto(file);

        // Update photo with analysis
        setPhotos(prev => prev.map(p => 
          p.id === photoId 
            ? { ...p, status: 'analyzed', analysis }
            : p
        ));

        // Auto-save to database if enabled
        if (autoSave) {
          let uploadResult = null;
          try {
            // Upload photo to S3 first (with deduplication)
            try {
              uploadResult = await photoUploadService.uploadPhoto(file);
              console.log('Photo uploaded to S3:', {
                photoUrl: uploadResult?.photoUrl,
                isDuplicate: uploadResult?.isDuplicate,
                fileHash: uploadResult?.fileHash?.substring(0, 16) + '...'
              });
            } catch (uploadError) {
              console.error('S3 upload failed:', uploadError);
              const uploadErrorMessage = uploadError?.message || uploadError?.response?.data?.error || 'Unknown upload error';
              throw new Error(`Photo upload failed: ${uploadErrorMessage}`);
            }
            
            const photoUrl = uploadResult?.photoUrl;
            const thumbnailUrl = uploadResult?.thumbnailUrl;
            
            if (!photoUrl) {
              throw new Error('Photo upload succeeded but no URL returned from server');
            }
            
            // Ensure analysis is an object (not wrapped in response structure)
            // The analysis should already be extracted from aiService.analyzePhoto
            const analysisToSave = analysis && typeof analysis === 'object' ? analysis : {};
            
            if (!analysisToSave || Object.keys(analysisToSave).length === 0) {
              console.warn('Analysis object is empty or invalid:', analysis);
              // Don't throw - still try to save, backend will handle it
            }
            
            console.log('Saving photo analysis to database:', {
              photoUrl,
              photoName: file.name,
              hasAnalysis: !!analysisToSave,
              analysisKeys: analysisToSave ? Object.keys(analysisToSave) : [],
              projectId: projectId || 'none',
              verificationRequestId: verificationRequestId || 'none'
            });
            
            const saved = await analysisStorageService.savePhotoAnalysis({
              photoUrl,
              photoName: file.name,
              thumbnailUrl: thumbnailUrl || photoUrl,
              analysis: analysisToSave,
              projectId: projectId || null,
              verificationRequestId: verificationRequestId || null,
              fileHash: uploadResult?.fileHash, // Pass hash for deduplication
              originalUploadId: uploadResult?.originalUploadId // Reference to original if duplicate
            });

            console.log('Photo analysis saved successfully:', saved.id);

            // Update photo with saved ID AND the durable storage URL.
            // Without photoUrl here, downstream consumers (e.g. the property
            // check submit) only see the ephemeral blob previewUrl, which the
            // server can't fetch — so the report PDF and AI enrichment get no
            // image. Propagate the real S3 URL so images embed and enrichment
            // can run.
            setPhotos(prev => prev.map(p =>
              p.id === photoId
                ? {
                    ...p,
                    savedId: saved.id,
                    status: 'saved',
                    photoUrl,
                    thumbnailUrl: thumbnailUrl || photoUrl
                  }
                : p
            ));
          } catch (saveError) {
            console.error('Error saving analysis:', saveError);
            const errorMessage = saveError?.message || saveError?.response?.data?.error || 'Unknown error';
            console.error('Save error details:', {
              message: errorMessage,
              error: saveError,
              photoName: file.name,
              hasUploadResult: !!uploadResult,
              hasPhotoUrl: !!uploadResult?.photoUrl,
              analysisKeys: analysis ? Object.keys(analysis) : null,
              fullError: saveError
            });
            
            // Store error message for display
            setPhotos(prev => prev.map(p =>
              p.id === photoId
                ? { 
                    ...p, 
                    status: 'save_failed',
                    errorMessage: errorMessage
                  }
                : p
            ));
            
            // Also set global error to show user
            setError(`Failed to save ${file.name}: ${errorMessage}`);
          }
        }

        // Callback with analysis
        if (onAnalysisComplete) {
          onAnalysisComplete({
            photoName: file.name,
            analysis,
            savedId: photoData.savedId
          });
        }
      } catch (err) {
        console.error('Error analyzing photo:', err);
        setError(`Failed to analyze ${file.name}`);
      }
    }

    setAnalyzing(false);
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleDeletePhoto = async (photoId) => {
    const photo = photos.find(p => p.id === photoId);
    
    // Delete from database if saved
    if (photo?.savedId) {
      try {
        await analysisStorageService.deleteAnalysis(photo.savedId);
      } catch (err) {
        console.error('Error deleting saved analysis:', err);
      }
    }

    // Remove from state
    setPhotos(prev => prev.filter(p => p.id !== photoId));

    // Revoke preview URL
    if (photo?.previewUrl) {
      URL.revokeObjectURL(photo.previewUrl);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'analyzing': return 'info';
      case 'analyzed': return 'warning';
      case 'saved': return 'success';
      case 'save_failed': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'analyzing': return 'Analyzing...';
      case 'analyzed': return 'Analyzed';
      case 'saved': return 'Saved';
      case 'save_failed': return 'Save Failed';
      default: return 'Unknown';
    }
  };

  // Calculate summary statistics
  const calculateSummary = () => {
    const analyzedPhotos = photos.filter(p => p.analysis);
    const savedPhotos = photos.filter(p => p.status === 'saved');
    const failedPhotos = photos.filter(p => p.status === 'save_failed');
    
    // Aggregate detected features
    const allFeatures = new Set();
    const allIssues = [];
    let totalQualityScore = 0;
    let totalSafetyScore = 0;
    let qualityCount = 0;
    let safetyCount = 0;
    let compliantCount = 0;
    let nonCompliantCount = 0;
    
    analyzedPhotos.forEach(photo => {
      const analysis = photo.analysis;
      
      // Collect features
      if (analysis.detectedFeatures) {
        analysis.detectedFeatures.forEach(f => allFeatures.add(f));
      }
      
      // Collect issues
      if (analysis.issues && Array.isArray(analysis.issues)) {
        allIssues.push(...analysis.issues);
      }
      
      // Aggregate scores
      if (analysis.qualityScore) {
        totalQualityScore += analysis.qualityScore;
        qualityCount++;
      }
      if (analysis.safetyScore) {
        totalSafetyScore += analysis.safetyScore;
        safetyCount++;
      }
      
      // Count compliance
      if (analysis.complianceStatus === 'COMPLIANT') {
        compliantCount++;
      } else if (analysis.complianceStatus === 'NON_COMPLIANT') {
        nonCompliantCount++;
      }
    });
    
    return {
      total: photos.length,
      analyzed: analyzedPhotos.length,
      saved: savedPhotos.length,
      failed: failedPhotos.length,
      avgQualityScore: qualityCount > 0 ? (totalQualityScore / qualityCount).toFixed(1) : null,
      avgSafetyScore: safetyCount > 0 ? (totalSafetyScore / safetyCount).toFixed(1) : null,
      compliantCount,
      nonCompliantCount,
      allFeatures: Array.from(allFeatures),
      allIssues: [...new Set(allIssues)], // Remove duplicates
      uniqueIssues: allIssues.length
    };
  };

  const summary = calculateSummary();

  return (
    <Box>
      {/* Upload Button */}
      <Box sx={{ mb: 3 }}>
        <input
          accept="image/*,video/mp4,video/quicktime,video/webm"
          style={{ display: 'none' }}
          id="photo-upload"
          multiple
          type="file"
          onChange={handlePhotoUpload}
          disabled={analyzing}
        />
        <label htmlFor="photo-upload">
          <Button
            variant="contained"
            component="span"
            startIcon={analyzing ? <CircularProgress size={20} /> : <CloudUpload />}
            disabled={analyzing}
            fullWidth
          >
            {analyzing ? 'Uploading…' : 'Upload Photos or Video (Max 10)'}
          </Button>
        </label>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Photos (JPG, PNG, HEIC) are AI-analysed • Video (MP4/MOV/WebM, max 100MB) is attached as evidence
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary View - Collapsible, only shows when photos are analyzed */}
      {summary.analyzed > 0 && (
        <Accordion 
          expanded={summaryExpanded} 
          onChange={() => setSummaryExpanded(!summaryExpanded)}
          sx={{ mb: 3 }}
        >
          <AccordionSummary
            expandIcon={summaryExpanded ? <ExpandMore /> : <ExpandMore />}
            sx={{
              bgcolor: summaryExpanded ? 'primary.50' : 'grey.50',
              '&:hover': { bgcolor: 'grey.100' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              <Summarize color="primary" />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Analysis Summary
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {summary.analyzed} of {summary.total} photos analyzed
                  {summary.saved > 0 && ` • ${summary.saved} saved`}
                  {summary.failed > 0 && ` • ${summary.failed} failed`}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                {summary.avgQualityScore && (
                  <Chip 
                    label={`Quality: ${summary.avgQualityScore}/10`} 
                    size="small" 
                    color="success"
                  />
                )}
                {summary.avgSafetyScore && (
                  <Chip 
                    label={`Safety: ${summary.avgSafetyScore}/10`} 
                    size="small" 
                    color="info"
                  />
                )}
                {summary.compliantCount > 0 && (
                  <Chip 
                    label={`${summary.compliantCount} Compliant`} 
                    size="small" 
                    color="success"
                    variant="outlined"
                  />
                )}
                {summary.nonCompliantCount > 0 && (
                  <Chip 
                    label={`${summary.nonCompliantCount} Non-Compliant`} 
                    size="small" 
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Stack>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {/* Full Analysis by Photo */}
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                Full Analysis by Photo
              </Typography>
              
              {photos.filter(p => p.analysis).map((photo, idx) => {
                const analysis = photo.analysis;
                const isLast = idx === photos.filter(p => p.analysis).length - 1;
                
                return (
                  <Box key={photo.id}>
                    <Card 
                      variant="outlined" 
                      sx={{ 
                        mb: isLast ? 0 : 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'grey.50'
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          {/* Photo Thumbnail */}
                          <Box sx={{ flexShrink: 0 }}>
                            <CardMedia
                              component="img"
                              image={photo.previewUrl}
                              alt={photo.name}
                              sx={{
                                width: 120,
                                height: 120,
                                objectFit: 'cover',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider'
                              }}
                            />
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ 
                                display: 'block', 
                                mt: 0.5,
                                textAlign: 'center',
                                maxWidth: 120,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              Photo {idx + 1}
                            </Typography>
                          </Box>
                          
                          {/* Analysis Details */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                              <Typography variant="subtitle2" fontWeight="bold" noWrap sx={{ flex: 1 }}>
                                {photo.name}
                              </Typography>
                              <Chip 
                                label={getStatusLabel(photo.status)} 
                                size="small" 
                                color={getStatusColor(photo.status)}
                              />
                            </Box>
                            
                            {/* Detected Features */}
                            {analysis.detectedFeatures && analysis.detectedFeatures.length > 0 && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" fontWeight="bold" color="text.secondary" gutterBottom>
                                  Detected Features:
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                  {analysis.detectedFeatures.map((feature, fIdx) => (
                                    <Chip
                                      key={fIdx}
                                      label={feature}
                                      size="small"
                                      variant="outlined"
                                      color="primary"
                                    />
                                  ))}
                                </Box>
                              </Box>
                            )}
                            
                            {/* Scores */}
                            {(analysis.qualityScore || analysis.safetyScore || analysis.complianceStatus) && (
                              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                {analysis.qualityScore && (
                                  <Chip 
                                    label={`Quality: ${analysis.qualityScore}/10`} 
                                    size="small" 
                                    color="success"
                                  />
                                )}
                                {analysis.safetyScore && (
                                  <Chip 
                                    label={`Safety: ${analysis.safetyScore}/10`} 
                                    size="small" 
                                    color="info"
                                  />
                                )}
                                {analysis.complianceStatus && (
                                  <Chip 
                                    label={analysis.complianceStatus} 
                                    size="small" 
                                    color={analysis.complianceStatus === 'COMPLIANT' ? 'success' : 'warning'}
                                  />
                                )}
                              </Box>
                            )}
                            
                            {/* Issues */}
                            {analysis.issues && analysis.issues.length > 0 && (
                              <Box>
                                <Typography variant="caption" fontWeight="bold" color="text.secondary" gutterBottom>
                                  Issues Found ({analysis.issues.length}):
                                </Typography>
                                <Box component="ul" sx={{ 
                                  margin: '4px 0 0 0', 
                                  paddingLeft: '20px', 
                                  fontSize: '13px',
                                  maxHeight: 100,
                                  overflowY: 'auto'
                                }}>
                                  {analysis.issues.map((issue, iIdx) => (
                                    <li key={iIdx} style={{ marginBottom: '2px' }}>
                                      <Typography variant="body2">{issue}</Typography>
                                    </li>
                                  ))}
                                </Box>
                              </Box>
                            )}
                            
                            {/* Additional Analysis Data */}
                            {(analysis.siteConditions || analysis.materials || analysis.conditionAssessment) && (
                              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                {analysis.siteConditions && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                    <strong>Site Conditions:</strong> {analysis.siteConditions}
                                  </Typography>
                                )}
                                {analysis.materials && analysis.materials.length > 0 && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                    <strong>Materials:</strong> {analysis.materials.join(', ')}
                                  </Typography>
                                )}
                                {analysis.conditionAssessment && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    <strong>Condition:</strong> {analysis.conditionAssessment}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                );
              })}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Photos Grid */}
      {photos.length > 0 && (
        <Grid container spacing={3}>
          {photos.map((photo, idx) => (
            <Grid item xs={12} sm={6} md={4} key={photo.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: '2px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  position: 'relative',
                  overflow: 'visible',
                  '&:hover': {
                    boxShadow: 4,
                    borderColor: 'primary.main',
                    transform: 'translateY(-2px)',
                    transition: 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                  }
                }}
              >
                {/* Item Number Badge */}
                <Chip
                  label={photo.isVideo ? `Video ${idx + 1}` : `Photo ${idx + 1}`}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    zIndex: 2,
                    bgcolor: 'primary.main',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                    height: '24px'
                  }}
                />
                {photo.isVideo ? (
                  <CardMedia
                    component="video"
                    height="200"
                    src={photo.photoUrl || photo.previewUrl}
                    controls
                    sx={{
                      objectFit: 'cover',
                      bgcolor: '#000',
                      borderBottom: '2px solid',
                      borderColor: 'divider'
                    }}
                  />
                ) : (
                  <CardMedia
                    component="img"
                    height="200"
                    image={photo.previewUrl}
                    alt={photo.name}
                    sx={{
                      objectFit: 'cover',
                      borderBottom: '2px solid',
                      borderColor: 'divider'
                    }}
                  />
                )}
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', pb: 2 }}>
                  {/* Photo Name & Status */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    mb: 1.5,
                    pb: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Typography variant="body2" fontWeight="medium" noWrap sx={{ flex: 1, mr: 1 }}>
                      {photo.name}
                    </Typography>
                    <Chip 
                      label={getStatusLabel(photo.status)} 
                      size="small" 
                      color={getStatusColor(photo.status)}
                      sx={{ flexShrink: 0 }}
                    />
                  </Box>

                  {/* Analysis Progress */}
                  {photo.status === 'analyzing' && (
                    <LinearProgress sx={{ mb: 2 }} />
                  )}

                  {/* Analysis Results - Compact View */}
                  {photo.analysis && Object.keys(photo.analysis).length > 0 ? (
                    <Box sx={{ mt: 1.5, flexGrow: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mb: 1 }}>
                        Analysis Results
                      </Typography>
                      
                      {/* Detected Features - Compact */}
                      {photo.analysis.detectedFeatures && photo.analysis.detectedFeatures.length > 0 && (
                        <Box sx={{ mb: 1.5 }}>
                          <Typography variant="caption" fontWeight="medium" color="text.secondary" gutterBottom>
                            Features:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {photo.analysis.detectedFeatures.slice(0, 3).map((feature, fIdx) => (
                              <Chip
                                key={fIdx}
                                label={feature}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: '20px' }}
                              />
                            ))}
                            {photo.analysis.detectedFeatures.length > 3 && (
                              <Chip
                                label={`+${photo.analysis.detectedFeatures.length - 3} more`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: '20px' }}
                              />
                            )}
                          </Box>
                        </Box>
                      )}
                      
                      {/* Scores */}
                      <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                        {photo.analysis.qualityScore && (
                          <Chip 
                            label={`Quality: ${photo.analysis.qualityScore}/10`} 
                            size="small" 
                            color="success"
                            sx={{ fontSize: '0.7rem', height: '22px' }}
                          />
                        )}
                        {photo.analysis.safetyScore && (
                          <Chip 
                            label={`Safety: ${photo.analysis.safetyScore}/10`} 
                            size="small" 
                            color="info"
                            sx={{ fontSize: '0.7rem', height: '22px' }}
                          />
                        )}
                        {photo.analysis.complianceStatus && (
                          <Chip 
                            label={photo.analysis.complianceStatus} 
                            size="small" 
                            color={photo.analysis.complianceStatus === 'COMPLIANT' ? 'success' : 'warning'}
                            sx={{ fontSize: '0.7rem', height: '22px' }}
                          />
                        )}
                      </Box>

                      {/* Issues - Compact */}
                      {photo.analysis.issues && photo.analysis.issues.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" fontWeight="medium" color="text.secondary">
                            Issues ({photo.analysis.issues.length}):
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ 
                            display: 'block', 
                            mt: 0.5,
                            fontSize: '0.7rem',
                            lineHeight: 1.4
                          }}>
                            {photo.analysis.issues.slice(0, 2).join(' • ')}
                            {photo.analysis.issues.length > 2 && ` • +${photo.analysis.issues.length - 2} more`}
                          </Typography>
                        </Box>
                      )}

                      {/* Auto-save indicator */}
                      {autoSave && photo.status === 'saved' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                          <CheckCircle sx={{ fontSize: 14, color: 'success.main' }} />
                          <Typography variant="caption" color="success.main">
                            Saved to library
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ) : photo.status === 'saved' ? (
                    <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.200' }}>
                      <Typography variant="caption" color="warning.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ErrorIcon sx={{ fontSize: 14 }} />
                        Analysis data not available for this photo
                      </Typography>
                    </Box>
                  ) : null}

                  {/* Error messages */}
                  {photo.status === 'save_failed' && (
                        <Box sx={{ mt: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <ErrorIcon sx={{ fontSize: 14, color: 'error.main' }} />
                            <Typography variant="caption" color="error.main" fontWeight="medium">
                              Failed to save
                            </Typography>
                          </Box>
                          {photo.errorMessage && (
                            <Typography variant="caption" color="error.main" sx={{ 
                              fontSize: '0.65rem',
                              display: 'block',
                              mt: 0.5,
                              pl: 2.5,
                              fontStyle: 'italic'
                            }}>
                              {photo.errorMessage}
                            </Typography>
                          )}
                        </Box>
                      )}

                  {/* Delete Button */}
                  <Box sx={{ 
                    mt: 'auto', 
                    pt: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    display: 'flex', 
                    justifyContent: 'flex-end' 
                  }}>
                    <Tooltip title="Delete photo">
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => handleDeletePhoto(photo.id)}
                        sx={{
                          '&:hover': {
                            bgcolor: 'error.light',
                            color: 'white'
                          }
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Empty State */}
      {photos.length === 0 && !analyzing && (
        <Box 
          sx={{ 
            textAlign: 'center', 
            py: 6, 
            border: '2px dashed',
            borderColor: 'grey.300',
            borderRadius: 2,
            bgcolor: 'grey.50'
          }}
        >
          <CloudUpload sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Photos Uploaded
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Upload photos to get instant AI analysis
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AIPhotoAnalyzerEnhanced;
